import { z } from 'zod'
import { countryEnum } from '../entities/country-enum'

/**
 * A recipient's VAT ID (USt-IdNr., UID-Nr., UID) as typed. Deliberately not format-checked: a
 * Swiss `CHE-123.456.789 MWST` or a French ID must never be refused. Empty means "none".
 */
export const recipientVatIdSchema = z.string().trim().max(30)

/**
 * An alternative addressee typed on a single document, replacing the job's counterpart as the
 * party the document is addressed to.
 *
 * Property-service companies enter the tenant of a property as the customer on the job, because
 * that is the address their crews drive to, while the party that owes the money is the
 * Hausverwaltung or the WEG behind it. The override names that party on the document without
 * touching the job, its Auftragsnummer or its counterpart link.
 *
 * The whole addressee is replaced, not part of it: if the Hausverwaltung is the contracting party
 * then it is the debtor, the VAT-relevant buyer party in the e-invoice, and the addressee of the
 * dunning. An attention line bolted onto the tenant's address would print a legally wrong
 * addressee, and leaving the email on the tenant would send the document to the person who must
 * not see it.
 */
export const recipientOverrideSchema = z.object({
  name: z.string().trim().min(1).max(200),
  /**
   * "z. Hd. …" — one string, so post to a large Hausverwaltung reaches the right desk. The print
   * layout only ever concatenates the snapshot's two name fields, so two inputs would buy nothing.
   */
  contactPerson: z.string().trim().max(200).optional(),
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).optional(),
  postalCode: z.string().trim().min(1).max(20),
  city: z.string().trim().min(1).max(100),
  /** Defaulted to the company's own country by the caller — the domestic case is almost always right. */
  country: z.enum(countryEnum.enumValues),
  /**
   * Optional: a recipient billed by post has none, and a missing address must not block the
   * document. Automated payment reminders are then skipped rather than falling back to the job
   * customer's address — see `process-payment-reminders`.
   */
  email: z.string().trim().max(320).optional(),
  /**
   * The override recipient's own VAT ID. There is no fallback to the counterpart's: one company's
   * VAT ID printed under another's address is worse than none.
   */
  vatId: recipientVatIdSchema.optional(),
})

export type RecipientOverride = z.infer<typeof recipientOverrideSchema>
